"use client";

import React from "react";
import { Zap, Layers, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  id: "speed",
  title: "Peer-Level Technical Precision",
  description: "Our care plans are engineered with the same rigor as a pre-flight checklist. We speak your language, from airpark logistics to medical certificate navigation.",
  icon: Zap,
  gradient: "from-primary/20 to-primary/5",
  iconColor: "text-primary"
}, {
  id: "curation",
  title: "The Right Seat Guarantee",
  description: "Transitioning from the left seat doesn't mean leaving the cockpit. We pair you with qualified pilots to keep you in the air and close to the aviation community.",
  icon: Layers,
  gradient: "from-secondary/20 to-secondary/5",
  iconColor: "text-secondary"
}, {
  id: "trust",
  title: "Aviation Exclusive Care",
  description: "A structured arrangement using home equity and aircraft assets to fund lifetime in-home care. No nursing homes. No depleted savings. Just a clear flight path.",
  icon: ShieldCheck,
  gradient: "from-primary/20 to-primary/5",
  iconColor: "text-primary"
}];
export default function WhyThisIsPilotToPilot() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="264958" id="why-this-is-pilot-to-pilot" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        {}
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)`,
        backgroundSize: '4rem 4rem',
        maskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, black 40%, transparent 100%)'
      }} />
        
        {}
        <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] bg-primary/5 rounded-lg blur-[120px] -translate-y-1/2 -translate-x-1/2 mix-blend-overlay" />
        <div className="absolute bottom-0 right-1/4 w-[40rem] h-[40rem] bg-secondary/10 rounded-lg blur-[120px] translate-y-1/2 translate-x-1/2 mix-blend-overlay" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-block px-3 py-1 mb-6 border border-primary/30 bg-primary/5 rounded-md">
            <span className="font-mono text-xs uppercase tracking-widest text-primary">Aviation Exclusive</span>
          </div>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Why This Is <span className="text-transparent bg-clip-text bg-gradient-to-r from-foreground to-primary">Pilot-to-Pilot</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default">
            Aging needs a flight plan. We provide the technical structure to keep you in your home and in the air, managed by those who understand the logbook of your life.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div key={feature.id} data-index={i} className={`transition-all duration-1000 ease-out delay-[${i * 200}ms] ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="h-full bg-card/30 backdrop-blur-md border-border/40 shadow-lg hover:border-primary/50 transition-all duration-500 relative overflow-hidden group rounded-md">
                  {}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
                  
                  <CardHeader className="relative z-10 pb-4">
                    <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-md bg-secondary/20 border border-border/50 shadow-sm backdrop-blur-md group-hover:border-primary/50 transition-all duration-500">
                      <Icon className={`w-7 h-7 ${feature.iconColor}`} strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-default text-xl tracking-tight uppercase">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <p className="text-muted-foreground leading-relaxed font-default">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}